import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  ContentCopy,
  MoreVert as MoreVertIcon
} from '@material-ui/icons'
import { deleteTalkById, getEventOrSubEventTalksOrPosters } from 'store/talk'
import { renderCol } from 'utils/renderColumns'
import ResourceNotFound from 'components/ResourceNotFound'
import getTableOptions from 'utils/getTableOptions'
import BasicTable from 'components/BasicTable'
import Wrapper from 'components/Wrapper'
import { Box } from '@mui/material'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import ConfirmModal from 'components/ConfirmationModal'
import ViewHeader from 'components/ViewHeader'
import { Button, IconButton, makeStyles, Menu, MenuItem, Paper, Tab, Tabs } from '@material-ui/core'
import { KeyboardArrowRight } from '@mui/icons-material'
import EventOrSessionInfo from 'components/EventOrSessionInfo'
import { toast } from 'utils/notify'

const app_base_url = window.location.origin

const academicsTabs = Object.freeze({
  POSTERS: 'posters',
  TALKS: 'talks'
})

const EventOrSubEventTalksOrPosters = () => {
  const classes = useStyles()
  const { sessionId } = useParams()
  const history = useHistory()
  const location = useLocation()

  // Read tab from URL params initially but manage locally
  const searchParams = new URLSearchParams(location.search)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || academicsTabs.TALKS)

  // Update active tab when the URL changes (e.g., when user navigates back)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tabFromUrl = params.get('tab')
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl)
    }
  }, [location.search])

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)

    // Update URL query parameter without causing a re-render
    const newSearchParams = new URLSearchParams(location.search)
    newSearchParams.set('tab', newValue)

    // Use history.replace to update URL without page reload or component re-render
    window.history.replaceState(null, '', `${location.pathname}?${newSearchParams.toString()}`)
  }

  const goBack = () => {
    history.goBack()
  }

  return (
    <>
      <ViewHeader title={`${sessionId ? 'Sub Event' : 'Event'} Talks/Posters`}>
        <Box style={{ display: 'flex', gap: '12px' }}>
          <Button variant="contained" color="primary" onClick={goBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<KeyboardArrowRight />}
            onClick={() =>
              history.push(
                `${location.pathname}/new?type=${
                  activeTab === academicsTabs.POSTERS ? 'poster' : 'talk'
                }`
              )
            }>
            Create
          </Button>
        </Box>
      </ViewHeader>

      <EventOrSessionInfo />
      <Box className={classes.contentWrapper}>
        <TalksOrPosters tab={activeTab} onTabChange={handleTabChange} />
      </Box>
    </>
  )
}

export default EventOrSubEventTalksOrPosters

const TalksOrPosters = ({ tab, onTabChange }) => {
  const classes = useStyles()
  const { eventId, sessionId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [loading, setLoading] = useState(false)
  const [deletedTalkId, setDeletedTalkId] = useState('')
  const [performingAction, setPerformingAction] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [currentTalk, setCurrentTalk] = useState(null)

  const { conference } = useSelector((state) => state.conference)
  const { talks, posters } = useSelector((state) => state.talk)

  // Define isPoster based on the tab prop
  const isPoster = tab === academicsTabs.POSTERS

  const fetchTalks = async () => {
    try {
      setLoading(true)
      await dispatch(getEventOrSubEventTalksOrPosters(eventId, sessionId, conference))
    } catch (error) {
      console.error('Error fetching talks:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (conference && conference.id) {
      console.log('fetching talks/posters')
      fetchTalks()
    }
  }, [conference.id])

  const handleDeleteTalk = async () => {
    try {
      setPerformingAction(true)
      await dispatch(deleteTalkById(deletedTalkId))
      await dispatch(getEventOrSubEventTalksOrPosters(eventId, sessionId, conference))
    } catch (error) {
      console.error('Error deleting talk:', error)
    } finally {
      setPerformingAction(false)
      setDeletedTalkId('')
    }
  }

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ width: '40px' }}>{value}</div>
      }
    },
    `${isPoster ? 'Poster #' : 'Abstract #'}`,
    'Title',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    {
      name: 'Building',
      options: {
        customBodyRender: (value) => renderCol(value)
      }
    },
    'Speakers',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]

  const formatTableData = (talks) =>
    talks?.map((talk) => {
      const talkNo =
        talk.isAbstract && talk.isPoster
          ? `${conference?.posterPrefix ? `${conference?.posterPrefix} - ` : ''}${
              talk.posterNumber
            }`
          : talk.isAbstract
          ? `${conference?.abstractPrefix ? `${conference?.abstractPrefix} - ` : ''}${
              talk.abstractNumber
            }`
          : ''
      return [
        talk?.srNo,
        talkNo,
        talk.title,
        talk.startDate,
        talk.endDate,
        talk?.buildingText,
        talk?.speakerNames,
        // talk?.pollId ? 'Yes' : 'No',
        <div style={{ textAlign: 'center' }}>
          <IconButton
            size="small"
            onClick={(event) => {
              setMenuAnchorEl(event.currentTarget)
              setCurrentTalk(talk)
            }}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl) && currentTalk?.id === talk?.id}
            onClose={() => {
              setMenuAnchorEl(null)
              setCurrentTalk(null)
            }}>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentTalk(null)
                history.push(`${location.pathname}/edit/${talk.id}`)
              }}>
              <EditIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentTalk(null)
                setDeletedTalkId(talk?.id)
              }}>
              <DeleteIcon fontSize="small" color="action" style={{ marginRight: 10 }} />
              Delete
            </MenuItem>
            {(talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : [])).map((pollId, index) => (
              <MenuItem
                key={pollId}
                onClick={() => {
                  setMenuAnchorEl(null)
                  setCurrentTalk(null)
                  navigator.clipboard.writeText(`${app_base_url}/polls/${pollId}`)
                  toast.info(`Poll ${index + 1} link copied to clipboard`, {
                    duration: 1000,
                    isClosable: true
                  })
                }}>
                <ContentCopy fontSize="small" color="action" style={{ marginRight: 10 }} />
                Copy Poll {index + 1} Link
              </MenuItem>
            ))}
          </Menu>
        </div>
      ]
    })

  const options = getTableOptions()

  return (
    <>
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={tab}
          onChange={onTabChange}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}>
          <Tab label="Talks" value={academicsTabs.TALKS} />
          <Tab label="Posters" value={academicsTabs.POSTERS} />
        </Tabs>
      </Paper>

      <Paper variant="outlined" style={{ marginTop: '16px' }}>
        {tab === academicsTabs.POSTERS && (
          <Wrapper inProgress={loading}>
            {posters?.length ? (
              <BasicTable data={formatTableData(posters)} columns={tableHead} options={options} />
            ) : (
              <ResourceNotFound message="No Poster available" />
            )}
          </Wrapper>
        )}
        {tab === academicsTabs.TALKS && (
          <Wrapper inProgress={loading}>
            {talks?.length ? (
              <BasicTable data={formatTableData(talks)} columns={tableHead} options={options} />
            ) : (
              <ResourceNotFound message="No Talk available" />
            )}
          </Wrapper>
        )}
      </Paper>
      <ConfirmModal
        dialogTitle={`Delete ${isPoster ? 'Poster' : 'Talk'}`}
        dialogText={`Are you sure you want to delete this ${isPoster ? 'poster' : 'talk'}?`}
        open={Boolean(deletedTalkId)}
        closeModal={() => setDeletedTalkId('')}
        handleConfirm={handleDeleteTalk}
        inProgress={performingAction}
      />
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    boxSizing: 'border-box'
  },
  tabsContainer: {
    marginTop: theme.spacing(3),
    borderRadius: 0,
    boxShadow: 'none',
    flexGrow: 1
  },
  tabs: {
    '& .MuiTab-root.MuiTab-labelIcon': {
      minHeight: '35px'
    },
    '& .MuiTab-root .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
      alignItems: 'center'
    },
    '& .MuiTab-root.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: 'rgba(51,220,235,0.16)'
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.primary.main,
      height: '2px'
    }
  }
}))
